import { Component, EventEmitter, OnInit } from '@angular/core';
import { NotificationService } from '../../../shared/notification.service';
import { FinancialAccount } from '../accounts';
import { FinancialAccountsService } from '../accounts.service';

@Component({
  selector: 'app-financial-accounts-list',
  templateUrl: 'list.component.html',
  styleUrls: [],
})
export class FinancialAccountsListComponent implements OnInit {
  /**
   * A list of all the financial-accountss currently loaded.
   *
   * @type {Accounts[]}
   */
  public financialAccounts: FinancialAccount[] = [];

  /**
   * Are we currently performing a loading action?
   *
   * @type {boolean}
   */
  public isLoading: boolean = true;

  /**
   * The total number of pages in this list of financial-accountss.
   *
   * @type {number}
   */
  public totalPages: number = 1;

  /**
   * The current page of financial-accountss that we are viewing.
   *
   * @type {number}
   */
  public currentPage: number = 1;

  /**
   * The total number of financial-accountss that can be viewed in the system.
   *
   * @type {number}
   */
  public totalResults: number = 0;

  /**
   * An Emitter to alert other components when a financialAccounts has been deleted.
   *
   * @type {EventEmitter<boolean>}
   */
  public financialAccountsDeleted: EventEmitter<boolean> = new EventEmitter<
    boolean
  >();

  /**
   * Load up all our required services and subscribe to every observable we need.
   *
   * @param {AccountsService} financialAccountsService
   * @param {NotificationService} notificationService
   */
  constructor(
    private financialAccountsService: FinancialAccountsService,
    private notificationService: NotificationService,
  ) {
    this.financialAccountsService.loading.subscribe(isLoading => {
      this.isLoading = isLoading;
    });
    this.financialAccountsService.totalPages.subscribe(totalPages => {
      this.totalPages = totalPages;
    });
    this.financialAccountsService.currentPage.subscribe(currentPage => {
      this.currentPage = currentPage;
    });
    this.financialAccountsService.totalResults.subscribe(totalResults => {
      this.totalResults = totalResults;
    });
  }

  /**
   * When the component is initialised we set it to load
   * page one of the financialAccounts list.
   */
  public ngOnInit(): void {
    this.loadFinancialAccounts(1);
  }

  /**
   * When we receive a page change from the pagination
   * component we load the appropriate page for the
   * financialAccounts list.
   *
   * @param {number} pageNumber
   */
  public onPageChanged(pageNumber: number): void {
    this.loadFinancialAccounts(pageNumber);
  }

  /**
   * Loads a list of financial-accountss using the given per page limit.
   *
   * @param {number} page The current page we require.
   * @param {number} limit The number of results per page.
   */
  public loadFinancialAccounts(page?: number, limit?: number): void {
    const listFinancialAccounts = this.financialAccountsService
      .listFinancialAccounts(page, limit)
      .subscribe((financialAccounts: FinancialAccount[]) => {
        this.financialAccounts = financialAccounts;
        listFinancialAccounts.unsubscribe();
      });
  }

  /**
   * When receiving confirmation of an item being deleted
   * we call the back end with the appropriate financialAccounts ID
   * to delete the financialAccounts.
   *
   * @param {string} id
   */
  public delete(id: string) {
    this.financialAccountsService.deleteFinancialAccounts(id).subscribe(
      deleted => {
        this.notificationService.success(
          `FinancialAccounts Deleted`,
          `FinancialAccounts with ID ${id} was deleted.`,
          5,
        );
        this.financialAccountsDeleted.next(true);
        this.onPageChanged(this.currentPage);
      },
      err => {
        this.notificationService.error(
          `FinancialAccounts Not Deleted`,
          err.error.message,
          10,
        );
        this.financialAccountsDeleted.next(false);
        this.onPageChanged(this.currentPage);
      },
    );
  }
}
